import './style.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function service_model_block() {
  const blocks = document.querySelectorAll('.service_model_block');
  if (!blocks.length) return;

  blocks.forEach(block => {
    const accordions = [...block.querySelectorAll('.accordions')];
    if (!accordions.length) return;

    const state = {
      index: 0,
      duration: 10,
      timeline: null,
      started: false,
      refreshCall: null,
      isOpen: true,
    };

    /* --------------------------------------------
     * Helpers
     * -------------------------------------------- */
    const getBody = acc => acc.querySelector('.accordion-body');
    const getProgress = acc => acc.querySelector('.progress-bar');
    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

    const delayedRefresh = () => {
      state.refreshCall?.kill();
      state.refreshCall = gsap.delayedCall(0.3, ScrollTrigger.refresh);
    };

    /* --------------------------------------------
     * Reset Accordions
     * -------------------------------------------- */
    function resetAccordions(activeIndex) {
      accordions.forEach((acc, i) => {
        const body = getBody(acc);
        const progress = getProgress(acc);

        acc.classList.toggle('accordion-active', i === activeIndex);

        if (body && i !== activeIndex) {
          gsap.set(body, { height: 0, overwrite: true });
        }

        if (progress) {
          gsap.set(progress, { width: 0 });
        }
      });
    }


    function resetToDefault(autoplay = false) {
      state.timeline?.kill();
      state.isOpen = true;
      state.index = 0;

      resetAccordions(0);

      const first = accordions[0];
      const body = getBody(first);
      const progress = getProgress(first);

      if (body) {
        gsap.fromTo(
          body,
          { height: 0 },
          {
            height: body.scrollHeight,
            duration: 0.35,
            overwrite: true,
            onComplete: delayedRefresh,
          }
        );
      }

      state.timeline = gsap.timeline({
        paused: !autoplay,
        onComplete: nextSlide,
      });

      if (progress) {
        state.timeline.to(progress, {
          width: '100%',
          duration: state.duration,
          ease: 'linear',
        });
      }

      if (autoplay) state.timeline.play();
    }

    /* --------------------------------------------
     * Close Accordion
     * -------------------------------------------- */
    function closeAccordion(index) {
      const acc = accordions[index];
      const body = getBody(acc);
      const progress = getProgress(acc);

      acc.classList.remove('accordion-active');
      state.isOpen = false;

      state.timeline?.pause();

      if (body) {
        gsap.to(body, {
          height: 0,
          duration: 0.25,
          overwrite: true,
          onComplete: delayedRefresh,
        });
      }

      if (progress) {
        gsap.set(progress, { width: 0 });
      }
    }

    /* --------------------------------------------
     * Open Accordion
     * -------------------------------------------- */
    function openAccordion(index, autoplay = true) {
      state.timeline?.kill();
      state.isOpen = true;

      const acc = accordions[index];
      const body = getBody(acc);
      const progress = getProgress(acc);

      resetAccordions(index);

      if (body) {
        gsap.fromTo(
          body,
          { height: 0 },
          {
            height: body.scrollHeight,
            duration: 0.35,
            overwrite: true,
            onComplete: delayedRefresh,
          }
        );
      }

      state.timeline = gsap.timeline({
        paused: !autoplay,
        onComplete: nextSlide,
      });

      if (progress) {
        state.timeline.to(progress, {
          width: '100%',
          duration: state.duration,
          ease: 'linear',
        });
      }

      if (autoplay) state.timeline.play();
    }

    function nextSlide() {
      state.index = (state.index + 1) % accordions.length;
      openAccordion(state.index);
    }

    /* --------------------------------------------
     * INIT
     * -------------------------------------------- */
    openAccordion(0, false);

    /* --------------------------------------------
     * Resize Handling
     * -------------------------------------------- */
    const handleResize = () => {
      if (!state.isOpen) return;
      const body = getBody(accordions[state.index]);
      if (!body) return;
      gsap.set(body, { height: body.scrollHeight });
      delayedRefresh();
    };

    let resizeRAF;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(handleResize);
    });

    /* --------------------------------------------
     * ScrollTrigger (per block)
     * -------------------------------------------- */
    ScrollTrigger.create({
      trigger: block,
      start: 'top bottom',
      end: 'bottom top',
      invalidateOnRefresh: true,

      onEnter: () => {
        state.started = true;
        if (state.isOpen) state.timeline?.play();
      },
      onEnterBack: () => {
        if (state.isOpen) state.timeline?.play();
      },
      onLeave: () => state.timeline?.pause(),
      onLeaveBack: () => state.timeline?.pause(),
    });

    /* --------------------------------------------
     * Click Handling
     * -------------------------------------------- */
    accordions.forEach((acc, index) => {
      acc.querySelector('.accordion-head')?.addEventListener('click', e => {
        e.preventDefault();

        const isSame = index === state.index && state.isOpen;

        if (isSame && isDesktop()) {
          resetToDefault(state.started);
          return;
        }

        if (isSame && !isDesktop()) {
          closeAccordion(index);
          return;
        }

        state.index = index;
        openAccordion(index, state.started);
      });
    });


  });
}
